import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Actionbuttons from 'components/common/actions-buttons';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IntlCurrencyFormat } from 'utils/helpers';
import { AccountingInvolvedParty } from 'v2-types/order';

import useAccountingContext from '../../../services/useAccountingContext';
import FieldRow from './field-row';
import InvolvedPartyAccounted from './involved-party-acocunted';

const useStyles = makeStyles((theme: any) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  party: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.button.default}`,
    borderRadius: theme.spacing(0.5),
  },
  partyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& button': {
      padding: theme.spacing(1.5),
    },
  },
  partyName: {
    minWidth: 0,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.primary.darker,
    overflowWrap: 'anywhere',
  },
  accountedBy: {
    marginTop: theme.spacing(1),
  },
  accountedByLabel: {
    marginBottom: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: theme.palette.primary.light,
  },
  empty: {
    marginLeft: theme.spacing(1.5),
    fontWeight: 500,
    color: theme.palette.tab.offselected,
  },
}));

type InvolvedPartiesListProps = {
  ledgerRow: any;
  handleEditLedgerAssociation: (row: any) => void;
  handleDeleteLedgerAssociation: (row: any) => void;
};

const InvolvedPartiesList: FC<InvolvedPartiesListProps> = ({
  ledgerRow,
  handleEditLedgerAssociation,
  handleDeleteLedgerAssociation,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const parties: AccountingInvolvedParty[] = ledgerRow?.involved_parties || [];
  const { id } = useParams<{ id: string }>();
  const { handledeleteLink } = useAccountingContext()!;

  const handleDeleteLink = useCallback(
    (paymentRecordId: string, associationKind: string, associationId: string) => {
      handledeleteLink(id, paymentRecordId, associationKind, associationId, ledgerRow.id);
    },
    [handledeleteLink, id, ledgerRow.id]
  );

  if (!parties.length) {
    return (
      <Typography variant="body2" className={classes.empty}>
        {t('contact:no-items')}
      </Typography>
    );
  }

  return (
    <Box className={classes.list}>
      {parties.map((party) => (
        <Box className={classes.party} key={party._id}>
          <Box className={classes.partyHeader}>
            <Typography component="div" className={classes.partyName}>
              {party.name}
            </Typography>
            <Actionbuttons
              handleEdit={() => handleEditLedgerAssociation({ row: party, ledgerInfo: ledgerRow })}
              handleDelete={() => handleDeleteLedgerAssociation({ row: party, ledgerInfo: ledgerRow })}
            />
          </Box>

          <FieldRow label={t('accounting:kind')} value={party.kind} />
          <FieldRow
            label={t('accounting:amount')}
            value={IntlCurrencyFormat(Number(party.amount) || 0)}
          />
          <FieldRow
            label={t('orders:beforeClosingHeader')}
            value={IntlCurrencyFormat(Number(party.before_closing_amount) || 0)}
          />
          <FieldRow
            label={t('orders:atClosingHeader')}
            value={IntlCurrencyFormat(Number(party.at_closing_amount) || 0)}
          />
          <FieldRow
            label={t('orders:paymentIdHeader')}
            value={party.payment_reference || t('orders:notDefinedYetLabel')}
          />

          <Box className={classes.accountedBy}>
            <Typography component="div" className={classes.accountedByLabel}>
              {t('orders:accountedByHeader')}
            </Typography>
            <InvolvedPartyAccounted row={party} handleDeleteLink={handleDeleteLink} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default InvolvedPartiesList;
